import { useTheme } from "@hooks/useTheme";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronUpIcon } from "@components/Icons";
import { PaginationEllipsis } from "@components/Pagination";

const splitComponentAndField = (text) => {
  const parts = text.split(":").map((s) => s.trim());
  if (parts.length < 2) {
    return { comp: "unknown", field: text };
  }
  return { comp: parts[0], field: parts.slice(1).join(":").trim() };
};

const groupByComponent = (list) => {
  const grouped = {};
  (list || []).forEach((item) => {
    const { comp, field } = splitComponentAndField(item);
    if (!grouped[comp]) grouped[comp] = [];
    grouped[comp].push(field);
  });
  return grouped;
};

const GroupedAccordion = ({
  list,
  itemsPerPage = 5,
  scanComponents = [],
  "data-testid": dataTestId = "grouped-accordion",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const grouped = groupByComponent(list);
  const [openComponents, setOpenComponents] = useState(new Set());
  const [page, setPage] = useState(1);
  const [goToPageValue, setGoToPageValue] = useState("1");

  const entries = Object.entries(grouped);
  const totalItems = entries.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const pagedEntries = entries.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const hasData = list && list.length > 0;

  // Calculate showing range
  const startItem = totalItems > 0 ? (page - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(page * itemsPerPage, totalItems);

  const toggle = (comp) => {
    const newSet = new Set(openComponents);
    if (newSet.has(comp)) {
      newSet.delete(comp);
    } else {
      newSet.add(comp);
    }
    setOpenComponents(newSet);
  };

  const handleGoToPage = (newPage) => {
    const pageNum = parseInt(newPage);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
      setGoToPageValue(pageNum.toString());
    }
  };

  const handleGoToPageInputChange = (e) => {
    const value = e.target.value;
    setGoToPageValue(value);
    if (value && !isNaN(value)) {
      handleGoToPage(value);
    }
  };

  // Update goToPageValue when page changes externally
  useEffect(() => {
    setGoToPageValue(page.toString());
  }, [page]);

  // Get pagination range with ellipsis
  const getPaginationRange = () => {
    const pages = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    const delta = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, page - delta);
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = totalPages - maxPagesToShow + 1;
    }

    if (startPage > 1) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      pages.push("...");
    }

    return pages;
  };

  return (
    <div className={`space-y-4 `} data-testid={dataTestId}>
      {!hasData ? (
        <div className={`text-center py-8 ${colors.textSubTitle}`} data-testid={`${dataTestId}-empty`}>
          {t("common.no-data-to-display", "No data to display.")}
        </div>
      ) : (
        pagedEntries.map(([comp, fields], index) => {
        // Get matching component from scanComponents using comp as index (comp - 1)
        const compIndex = parseInt(comp) - 1;
        const matchingComponent = scanComponents && scanComponents.length > compIndex && compIndex >= 0
          ? scanComponents[compIndex]
          : null;
        
        const componentName = matchingComponent?.componentName || matchingComponent?.component || null;
        const componentVersion = matchingComponent?.componentVersion || null;
        const componentLicense = matchingComponent?.licenseName || matchingComponent?.componentLicense || matchingComponent?.licenseIdentifier || null;
        
        return (
          <div
            key={comp}
            data-testid={`${dataTestId}-group-${comp}`}
            className={`flex-1 overflow-hidden border ${colors.borderPrimary} ${colors.bgPrimaryDarker} px-4 rounded-2xl shadow-[0px_12px_24px_rgba(0,0,0,0.2)]`}
          >
            {/* Header */}
            <button
              type="button"
              data-testid={`${dataTestId}-group-${comp}-toggle`}
              className={`w-full flex justify-between items-center  py-3 text-sm font-medium rounded-t-2xl transition ${colors.textPrimary} text-left`}
              onClick={() => toggle(comp)}
            >
              <div className="flex flex-col items-start flex-1 min-w-0 text-left">
                <div className={`text-sm font-medium w-full text-left flex items-start ${colors.textPrimary}`}>
                  <span className="flex-shrink-0 mr-2">{comp}. </span>
                  <div className="flex flex-col flex-1 min-w-0">
                    <div>
                      <span className={`${colors.textPrimary}`}>{componentName}</span>
                      <span className={colors.iconMuted}>
                        {` (`}
                        {componentVersion && (
                          componentVersion.length > 15 ? (
                            <span className="truncate max-w-[120px] inline-block align-bottom" title={componentVersion}>
                              {componentVersion}
                            </span>
                          ) : (
                            componentVersion
                          )
                        )}
                        {`)`}
                      </span>
                    </div>
                    {componentLicense && (
                      <div className={`mt-0.5 text-xs ${colors.iconMuted}`}>
                        {componentLicense}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <ChevronUpIcon
                className={`w-6 h-6 transition-transform duration-200 ${colors.textSecondary} ${openComponents.has(comp) ? "" : "rotate-180"}`}
              />
            </button>

            {/* Content */}
            <div
              data-testid={`${dataTestId}-group-${comp}-content`}
              className={`overflow-hidden transition-[max-height] rounded-2xl duration-300 ease-in-out ${openComponents.has(comp) ? 'mb-3' : ''}`}
              style={{
                maxHeight: openComponents.has(comp) ? `${fields.length * 2.5}rem` : "0",
              }}
            >
              <ul className={`${colors.bgDocumentTable}`} data-testid={`${dataTestId}-group-${comp}-list`}>
                {fields.map((f, idx) => (
                  <li
                    key={idx}
                    data-testid={`${dataTestId}-group-${comp}-field-${idx}`}
                    className={`py-2 px-4 text-sm font-medium transition-colors text-left ${colors.textPrimary} ${idx % 2 === 0 ? colors.bgTertiary : colors.bgPrimary}`}
                  >
                    - {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
        })
      )}

      {/* Pagination */}
      {hasData && totalPages > 1 && (
        <div className="flex items-center justify-between gap-4 flex-wrap mt-4" data-testid={`${dataTestId}-pagination`}>
          {/* Left side: Go to page and Showing info */}
          <div className="flex items-center gap-4">
            {/* Go to page */}
            <div className="flex items-center gap-2 text-sm">
              <span className={`${colors.textSubTitle} text-xs font-medium`}>
                {t("common.table.go-to-page", "Go to page")}
              </span>
              <input
                type="number"
                data-testid={`${dataTestId}-pagination-go-to`}
                min="1"
                max={totalPages}
                value={goToPageValue}
                onChange={handleGoToPageInputChange}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (!value || isNaN(value) || parseInt(value) < 1) {
                    setGoToPageValue(page.toString());
                  }
                }}
                className={`h-8 w-16 rounded border px-2 py-1 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary}`}
              />
            </div>

            {/* Showing info */}
            <div className="flex items-center text-xs">
              <span className={colors.textPrimary}>
                {t("common.table.showing", "Showing")} {startItem} - {endItem} {t("common.table.of", "of")} {totalItems}
              </span>
            </div>
          </div>

          {/* Right side: Navigation buttons */}
          <div className="flex items-center gap-1">
            {/* Previous Page Button */}
            <button
              type="button"
              data-testid={`${dataTestId}-pagination-prev`}
              onClick={() => {
                const newPage = Math.max(page - 1, 1);
                setPage(newPage);
                setGoToPageValue(newPage.toString());
              }}
              disabled={page === 1}
              className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-30`}
              title="Previous page"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.prev", "Prev")}</span>
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {getPaginationRange().map((pageNum, index) => (
                <div key={index}>
                  {pageNum === "..." ? (
                    <PaginationEllipsis
                      dataTestId={`${dataTestId}-pagination-ellipsis-${index}`}
                      className={`inline-flex items-center justify-center rounded border px-3 py-1 text-sm ${colors.borderPaginationButton} ${colors.textPrimary} ${colors.bgInput}`}
                    />
                  ) : (
                    <button
                      type="button"
                      data-testid={`${dataTestId}-pagination-page-${pageNum}`}
                      onClick={() => {
                        setPage(pageNum);
                        setGoToPageValue(pageNum.toString());
                      }}
                      className={`rounded border px-3 py-1 text-sm transition-colors ${colors.borderPaginationButton} ${colors.textPrimary} ${pageNum === page ? `${colors.bgPaginationButtonActive}` : `${colors.bgInput}`}`}
                    >
                      {pageNum}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Next Page Button */}
            <button
              type="button"
              data-testid={`${dataTestId}-pagination-next`}
              onClick={() => {
                const newPage = Math.min(page + 1, totalPages);
                setPage(newPage);
                setGoToPageValue(newPage.toString());
              }}
              disabled={page === totalPages}
              className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-30`}
              title="Next page"
            >
              <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.next", "Next")}</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedAccordion;